import React from 'react';
import { useForm } from 'react-final-form';

import useWizardContext from '@swf/core/hooks/useWizardContext';

import type { InstantBonusInfo, PreCashinInfo } from '../../../types/Cashier';
import { BonusFlags } from '../../../utils/bonus';
import Prompt from '../../Prompt/Prompt';

import {
  TXTCLI_Claim_your_FREE_Instant_Bonus,
  TXTCLI_Confirm_Instant_Bonus_Purchase,
  TXTCLI_Instant_Cash_BonusX_N0X,
  TXTCLI_Tournament_Ticket_BonusX_N0X,
  TXTWCL_Accept_bonus,
  TXTWCL_Confirm_Your_Bonus,
  TXTWCL_Deposit_without_bonus
} from './BonusAcknowledgement.messages.json';
import ComposedBonusMessage from './components/ComposedBonusMessage/ComposedBonusMessage';
import DefaultBonusMessage from './components/DefaultBonusMessage/DefaultBonusMessage';
import DepositPartAsBonus from './components/DepositPartAsBonus/DepositPartAsBonus';
import InstantBonus from './components/InstantBonus/InstantBonus';
import InstantCashAndTournamentBonus from './components/InstantCashAndTournamentBonus/InstantCashAndTournamentBonus';
import SeeDetailsLink from './components/SeeDetailsLink/SeeDetailsLink';
import SplitWager from './components/SplitWager/SplitWager';
import StandardCashReleaseBonus from './components/StandardCashReleaseBonus/StandardCashReleaseBonus';
import TournamentTickets from './components/TournamentTickets/TournamentTickets';

interface Props {
  onClose(): void;
}

const getTitle = (
  {
    instantBonusInfo,
    fppToExercise,
    bonusMax,
    bonusName = '',
    bonusPercent
  }: PreCashinInfo = {} as PreCashinInfo
) => {
  switch (true) {
    case instantBonusInfo && instantBonusInfo.splitWager:
      return { translation: TXTWCL_Confirm_Your_Bonus };
    case instantBonusInfo && instantBonusInfo.cashPrice !== 0:
      return { translation: TXTCLI_Confirm_Instant_Bonus_Purchase };
    case !!instantBonusInfo:
      return { translation: TXTCLI_Claim_your_FREE_Instant_Bonus };
    case fppToExercise === 0 && !!(bonusMax || bonusPercent):
      return { translation: TXTCLI_Instant_Cash_BonusX_N0X, values: { 0: bonusName } };
    case fppToExercise === 0:
      return { translation: TXTCLI_Tournament_Ticket_BonusX_N0X, values: { 0: bonusName } };
    default:
      return { translation: { id: bonusName, defaultMessage: bonusName, track: bonusName } };
  }
};

const getContent = ({
  balanceAmount,
  bonusEnd,
  bonusName,
  instantBonus,
  instantBonusInfo,
  fppToExercise,
  bonusPercent,
  bonusMax,
  potentialBonusAmount,
  bonusFlags,
  maxPossibleCashInAmount
}: PreCashinInfo) => {
  const calculatedBonusAmount = (balanceAmount * bonusPercent) / 100.0;

  switch (true) {
    case instantBonus && instantBonusInfo?.splitWager:
      return (
        <SplitWager
          balanceAmount={balanceAmount}
          potentialBonusAmount={potentialBonusAmount}
          bonusName={bonusName}
          instantBonusInfo={instantBonusInfo as InstantBonusInfo}
          bonusEnd={bonusEnd}
        />
      );
    case instantBonus && !!instantBonusInfo:
      return <InstantBonus />;
    case fppToExercise === 0:
      return <InstantCashAndTournamentBonus />;
    // eslint-disable-next-line no-bitwise
    case !!(bonusFlags & BonusFlags.TournamentTicketOnly):
      return <TournamentTickets />;
    case calculatedBonusAmount === bonusMax || balanceAmount >= maxPossibleCashInAmount:
      return (
        <>
          <ComposedBonusMessage
            isFirst={false}
            amount={balanceAmount}
            bonus={Math.min(bonusMax, potentialBonusAmount)}
          />
          <SeeDetailsLink />
        </>
      );
    case calculatedBonusAmount > bonusMax:
      return <DepositPartAsBonus />;
    // eslint-disable-next-line no-bitwise
    case !!(bonusFlags & BonusFlags.GradualDepositAllowed):
      return <StandardCashReleaseBonus />;
    default:
      return <DefaultBonusMessage />;
  }
};

const BonusAcknowledgement: React.FC<Props> = ({ onClose }) => {
  const {
    data,
    values: { deposit }
  } = useWizardContext();
  const { preCashinInfo } = data;

  const form = useForm();

  const submitAndAcceptBonus = () => {
    deposit.bonusAcknowledged = true;
    form.submit();
  };

  const submitAndDismissBonus = () => {
    delete data.preCashinInfo;
    deposit.bonusCode = '';
    form.submit();
  };

  return (
    <Prompt
      variant="default"
      onClose={onClose}
      onPrimaryButtonClick={submitAndAcceptBonus}
      onSecondaryButtonClick={submitAndDismissBonus}
      title={getTitle(preCashinInfo)}
      primaryButton={{
        translation: TXTWCL_Accept_bonus
      }}
      secondaryButton={{ translation: TXTWCL_Deposit_without_bonus }}
    >
      {preCashinInfo && getContent(preCashinInfo)}
    </Prompt>
  );
};
export default BonusAcknowledgement;
